// VideoGallery.jsx

import React, { useState, useEffect } from 'react';
import { FiSearch, FiPlay } from 'react-icons/fi';
import ApiStore from '../../../api/ApiStore';
import VideoPlayer from './VideoPlayer'; // Adjust the path if needed
import { RingLoader } from 'react-spinners';
import { inject, observer } from 'mobx-react';

const VideoGallery = inject('store')(
  observer(({ store }) => {
    const [videos, setVideos] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedTag, setSelectedTag] = useState('');
    const [currentVideo, setCurrentVideo] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const fetchVideos = async () => {
      try {
        setIsLoading(true);
        const organizationName = store.profile.organization || 'Madel';
        const applicationName = 'Virtus';

        // Get videos settings from org settings
        const sequentialViewEnabled =
          store.orgSettings?.configVideos?.sequentialViewEnabled || false;
        const minViewsToUnlock =
          store.orgSettings?.configVideos?.minViewsToUnlock || 1;

        const videosData = await ApiStore.listVideos(
          organizationName,
          applicationName
        );
        const localImages = [
          '/video_cover.jpg',
          '/video_cover_2.jpg',
          '/video_cover_3.jpg',
          '/video_cover_4.jpg',
          '/video_cover_5.jpg',
          '/video_cover_6.jpg',
          '/video_cover_7.jpg',
          '/video_cover_8.jpg',
          '/video_cover_9.jpg',
          '/video_cover_10.jpg',
          '/video_cover_11.jpg',
        ];

        let videosWithViews = await Promise.all(
          videosData.map(async (video, index) => {
            const viewStats = await ApiStore.getVideoViews(
              video.external_id,
              organizationName
            );
            const randomImage =
              localImages[Math.floor(Math.random() * localImages.length)];

            return {
              ...video,
              views: viewStats.view_count,
              lastViewedDate: viewStats.last_viewed_date,
              backgroundImage: randomImage,
              index: index, // Store the original index for sorting
              isLocked: false, // Default value, will be updated below if sequential mode is enabled
            };
          })
        );

        // If sequential viewing is enabled, determine which videos are locked
        if (sequentialViewEnabled) {
          // Sort videos by index to ensure the original order
          videosWithViews.sort((a, b) => a.index - b.index);

          // First video is always unlocked
          videosWithViews[0].isLocked = false;

          // For each subsequent video, check if the previous video has enough views
          for (let i = 1; i < videosWithViews.length; i++) {
            const previousVideoViews = videosWithViews[i - 1].views;
            videosWithViews[i].isLocked = previousVideoViews < minViewsToUnlock;
          }
        }

        setVideos(videosWithViews);
      } catch (error) {
        console.error('Error fetching videos:', error);
      } finally {
        setIsLoading(false);
      }
    };

    useEffect(() => {
      // Only fetch videos if orgSettings is loaded
      if (store.orgSettings) {
        fetchVideos();
      }
    }, [store.orgSettings]);

    // Function to update a specific video's view count
    const updateVideoViewCount = (externalId, newViewCount) => {
      setVideos(currentVideos => {
        return currentVideos.map(v => {
          if (v.external_id === externalId) {
            return { ...v, views: newViewCount };
          }
          return v;
        });
      });
    };

    const playVideo = async video => {
      // Check if video is locked
      if (video.isLocked) {
        // Show alert instead of playing locked video
        alert(
          'Este video está bloqueado. Primero debes completar los videos anteriores.'
        );
        return;
      }

      try {
        const data = await ApiStore.getSignedUrl(video.external_id);
        const signedUrl = data.signed_url;

        setCurrentVideo({
          ...video,
          signedUrl,
          onUpdateViewCount: updateVideoViewCount,
        });
      } catch (error) {
        console.error('Error playing video:', error);
      }
    };

    const tags = Array.from(new Set(videos.flatMap(video => video.tags)));

    const filteredVideos = videos.filter(video => {
      const matchesSearch =
        video.video_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        video.tags.some(tag =>
          tag.toLowerCase().includes(searchTerm.toLowerCase())
        );
      const matchesTag = selectedTag ? video.tags.includes(selectedTag) : true;
      return matchesSearch && matchesTag;
    });

    if (currentVideo) {
      // Render the VideoPlayer component when a video is selected
      return (
        <div className="min-h-screen bg-gray-100 flex flex-col">
          <VideoPlayer
            video={currentVideo}
            onClose={() => {
              setCurrentVideo(null);

              // Check if we need to update the sequential unlock status
              const sequentialViewEnabled =
                store.orgSettings?.configVideos?.sequentialViewEnabled || false;
              const minViewsToUnlock =
                store.orgSettings?.configVideos?.minViewsToUnlock || 1;

              if (sequentialViewEnabled) {
                // Update the locked status for videos based on current view counts
                setVideos(currentVideos => {
                  // Sort videos by index to ensure the original order
                  const sortedVideos = [...currentVideos].sort(
                    (a, b) => a.index - b.index
                  );

                  // First video is always unlocked
                  sortedVideos[0].isLocked = false;

                  // For each subsequent video, check if the previous video has enough views
                  for (let i = 1; i < sortedVideos.length; i++) {
                    const previousVideoViews = sortedVideos[i - 1].views;
                    sortedVideos[i].isLocked =
                      previousVideoViews < minViewsToUnlock;
                  }

                  return sortedVideos;
                });
              }
            }}
          />
        </div>
      );
    }

    if (isLoading) {
      return (
        <div className="flex justify-center items-center h-screen bg-gray-100">
          <RingLoader color="#000000" size={120} />
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-gray-100 flex flex-col">
        <div className="flex-grow p-8 max-w-6xl mx-auto">
          <h1 className="text-4xl font-bold mb-8 text-gray-800 text-center">
            Galería de Videos
          </h1>
          <div className="mb-8 relative max-w-2xl mx-auto">
            <input
              type="text"
              placeholder="Buscar videos..."
              className="w-full p-4 pr-12 rounded-lg border-2 border-gray-300 focus:outline-none focus:border-blue-500 transition-colors"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
            <FiSearch className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-xl" />
          </div>
          <div className="mb-8 flex flex-wrap gap-4 justify-center">
            {tags.map(tag => (
              <button
                key={tag}
                className={`px-4 py-2 rounded-full text-sm font-medium ${
                  selectedTag === tag
                    ? 'bg-blue-500 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-200'
                } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors`}
                onClick={() => setSelectedTag(selectedTag === tag ? '' : tag)}
              >
                {tag}
              </button>
            ))}
          </div>
          {filteredVideos.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredVideos.map(video => {
                // Determine gradient colors based on progress
                let gradientFrom = '';
                let gradientTo = '';
                let backdropFilter = '';
                if (video.views === 0) {
                  // Unseen video
                  gradientFrom = 'rgba(128, 128, 128, 0.5)'; // Gray with 50% opacity
                  gradientTo = 'rgba(192, 192, 192, 0.5)';
                  backdropFilter = 'blur(5px)';
                } else if (video.views >= 5 && video.views < 7) {
                  // Seen video with ≥80% correct answers
                  gradientFrom = 'rgba(56, 161, 105, 0.4)'; // Green with 50% opacity
                  gradientTo = 'rgba(72, 187, 120, 0.4)';
                  backdropFilter = 'blur(0px)';
                } else if (video.views >= 7) {
                  // Seen video with ≥80% correct answers
                  gradientFrom = 'rgba(56, 161, 105, 0.0)'; // Green with 50% opacity
                  gradientTo = 'rgba(72, 187, 120, 0.0)';
                  backdropFilter = 'blur(0px)';
                } else {
                  // Seen video with <80% correct answers
                  // gradientFrom = 'rgba(220, 38, 38, 0.5)'; // Red with 50% opacity
                  // gradientTo = 'rgba(239, 68, 68, 0.5)';
                  gradientFrom = 'rgba(72, 187, 120, 0.5)'; // Gray with 50% opacity
                  gradientTo = 'rgba(192, 192, 192, 0.5)';
                  backdropFilter = 'blur(1.5px)';
                }

                // Determine if the video is locked
                const isLocked = video.isLocked;

                // Modify the gradient based on locked status
                if (isLocked) {
                  gradientFrom = 'rgba(75, 85, 99, 0.85)'; // Gray with high opacity
                  gradientTo = 'rgba(75, 85, 99, 0.85)';
                  backdropFilter = 'blur(4px)';
                }

                return (
                  <div
                    key={video.video_id}
                    className={`bg-white rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow ${isLocked ? 'opacity-75' : ''}`}
                  >
                    <div
                      className="w-full h-48 relative flex items-center justify-center"
                      style={{
                        backgroundImage: `url(${video.backgroundImage})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                      }}
                    >
                      {/* Gradient overlay with glass effect */}
                      <div
                        className="absolute inset-0"
                        style={{
                          background: `linear-gradient(to right, ${gradientFrom}, ${gradientTo})`,
                          backdropFilter: backdropFilter, // For glass effect
                        }}
                      ></div>
                      {/* Overlay statistics */}
                      <div className="relative text-white text-center z-10">
                        {isLocked ? (
                          <div className="flex flex-col items-center">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-8 w-8 mb-2"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                              />
                            </svg>
                            <p className="text-lg font-semibold">Bloqueado</p>
                          </div>
                        ) : video.views === 0 ? (
                          <p className="text-xl font-semibold">No visto</p>
                        ) : (
                          <>
                            {/* <p className="text-xl font-semibold">
                            {video.percentage}% Correctas
                          </p> */}
                            <p className="text-xl">
                              {video.views} Visualizaciones
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="p-6">
                      <h2 className="text-xl font-semibold mb-2 text-gray-800">
                        {video.video_name}
                      </h2>
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-blue-500">
                          {video.tags.join(', ')}
                        </span>
                        <div className="flex space-x-2">
                          <button
                            className={`p-2 rounded-full ${isLocked ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600'} text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors`}
                            aria-label={
                              isLocked ? 'Video bloqueado' : 'Reproducir video'
                            }
                            onClick={() => playVideo(video)}
                            disabled={isLocked}
                          >
                            <FiPlay />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-center text-gray-600">
              No se encontraron videos.
            </p>
          )}
        </div>
      </div>
    );
  })
);

export default VideoGallery;
